import useSWR from 'swr';
import api from '../../services/api';
import { fetcher } from '../../services/fetcher';
import { useAuth } from '../../contexts/AuthContext';
import { moduleConfig } from './config';
import type { ParsedContactsImportRow } from './utils/parseContactsSpreadsheet';

export interface Client {
  clientId: number;
  clientName?: string;
  clientName2?: string;
  clientCode?: string;
  contactPerson?: string;
  contactPosition?: string;
  clientAddress?: string;
  clientCity?: string;
  clientZipCode?: string;
  clientState?: string;
  clientCountryId?: number;
  clientCountryDescr?: {
    pc_descr?: string;
  };
  clientWebSite?: string;
  contactPoints?: import('../address-book/contactPoints').ContactPoint[];
  clientNotes?: string;
  clientVATNumber?: string;
  clientPaymentTerms?: number;
  clientTaxAuthority?: number;
  gemiNumber?: string;
  dateStarted?: string;
  sourceUrl?: string;
  clientCalls?: Array<{
    clientCallDate?: string | null;
  }>;
  _count?: {
    clientCalls?: number;
    clientReminders?: number;
    clientAppointments?: number;
    clientTasks?: number;
    clientServices?: number;
    FileAttachment?: number;
  };
  clientCreatedAt: string;
  clientUpdatedAt?: string;
  FileAttachment?: Array<{
    id: number;
    fileName?: string;
    filePath?: string;
    fileType?: string;
    fileSize?: number;
    uploadedAt?: string;
  }>;
}

export interface ClientsResponse {
  data: Client[];
  total: number;
  page: number;
  limit: number;
}

// Normalize list response (backend may return array or { data: [] } / { records: [] })
function toClientsList(raw: unknown): Client[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as ClientsResponse).data)) {
    return (raw as ClientsResponse).data;
  }
  if (raw && typeof raw === 'object' && 'records' in raw && Array.isArray((raw as { records: Client[] }).records)) {
    return (raw as { records: Client[] }).records;
  }
  return [];
}

export const useClients = (searchParams?: string, enabled = true) => {
  const { can, permissionsLoaded } = useAuth();
  const canViewClients = permissionsLoaded && can('clients', 'view');
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const { data, error, isLoading, mutate } = useSWR<Client[] | ClientsResponse>(
    enabled && canViewClients ? `${moduleConfig.module.api.list}${queryString}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  const clients = toClientsList(data);
  return {
    data: clients,
    clients,
    total: clients.length,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useClient = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Client>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data,
    client: data,
    isLoading,
    isError: error,
    mutate,
  };
};

/** Summary response from GET /crm-clients/:id/summary (client + _count for all relations). */
export interface ClientSummaryCounts {
  clientAppointments?: number;
  clientTodos?: number;
  crm_ClientsContacts?: number;
  FileAttachment?: number;
  linkedEmails?: number;
}

/** KPIs returned by GET /crm-clients/:id/summary (kpis key). */
export interface ClientSummaryKpis {
  nextAppointmentDate: string | null;
}

export interface ClientSummary extends Omit<Client, '_count'> {
  _count?: ClientSummaryCounts;
  /** From summary endpoint */
  clientTaxAuthorityDescr?: { pt_descr?: string } | null;
  /** Aggregated KPIs for summary modal */
  kpis?: ClientSummaryKpis;
}

const CLIENT_SUMMARY_CACHE_MS = 60_000; // 1 min – avoid refetching too often when reopening modal

export const useClientSummary = (clientId: number | null) => {
  const url = clientId ? `${moduleConfig.module.api.detail(clientId)}/summary` : null;
  const { data, error, isLoading, mutate } = useSWR<ClientSummary | null>(
    url,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: CLIENT_SUMMARY_CACHE_MS,
      revalidateIfStale: true,
    }
  );
  return { data, clientSummary: data, isLoading, isError: error, mutate };
};

export type ImportContactsPreviewRow = {
  recordId: string;
  sourceName: string;
  clientName: string | null;
  contactPerson: string | null;
  clientAddress?: string | null;
  emailCount?: number;
  phoneCount?: number;
  status: 'ready' | 'duplicate_db' | 'duplicate_file' | 'invalid';
  duplicate: boolean;
  duplicateReason: 'exists' | 'duplicate_in_file' | null;
  issues?: string[];
};

export type ImportContactsPreviewSummary = {
  total: number;
  readyToImport: number;
  duplicateInDb: number;
  duplicateInFile: number;
  duplicateCount: number;
  invalid: number;
  withEmail: number;
  withPhone: number;
  withAddress: number;
  existingInDatabase: number;
  canImport: boolean;
};

export type ImportContactsPreviewResult = {
  rows: ImportContactsPreviewRow[];
  summary: ImportContactsPreviewSummary;
};

export type ImportContactsResult = {
  created: number;
  skipped: number;
  failed: number;
  errors: Array<{ recordId: string; message: string }>;
};

export async function previewClientsImport(
  rows: ParsedContactsImportRow[],
): Promise<ImportContactsPreviewResult> {
  const res = await api.post<ImportContactsPreviewResult>('/crm-clients/import/contacts/preview', {
    rows,
  });
  return res.data;
}

export async function importClientsFromContacts(
  rows: ParsedContactsImportRow[],
  skipDuplicates = true,
): Promise<ImportContactsResult> {
  const res = await api.post<ImportContactsResult>('/crm-clients/import/contacts', {
    rows,
    skipDuplicates,
  });
  return res.data;
}
